import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { PageHero, HeroLink } from "./PageHero";
import { RunConsole, runs } from "./ProductVisuals";
import { Card, CardContent } from "./ui/card";
import { Mark } from "./Mark";
import { PlatformGraphic } from "./PlatformGraphics";
import { siteConfig } from "../config/siteConfig";
import {
  Workflow,
  Zap,
  Users,
  Brain,
  Database,
  Plug,
  ArrowRight,
  ArrowUpRight,
  CheckCircle,
  Cloud,
  Server,
  Building2,
  Cpu,
  Lock,
  ChevronRight,
  MessageSquare,
  Mic,
  FileOutput,
} from "lucide-react";

interface PlatformPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// The 80/20 problem — infrastructure, not the model, is why POCs stall.
const infraGaps = ["Identity", "Memory", "Execution", "Messaging", "Storage", "Observability"];

const canvas = [
  { icon: Zap, label: "Trigger", desc: "Events, schedules, uploads, API calls" },
  { icon: Workflow, label: "Orchestrate", desc: "Agents & squads reason and call tools" },
  { icon: Users, label: "Human-in-the-loop", desc: "Review and approve where it matters" },
  { icon: CheckCircle, label: "Act", desc: "Write back to your systems" },
];

// Three layers. One coherent system. (Connect / Orchestrate / Deploy, as on ariviti.com/platform)
const layers = [
  {
    icon: Plug,
    title: "Connect",
    desc: "Works with the stack you already have. REST API nodes, MCP servers, and enterprise connectors — healthcare records, BFSI data platforms, HR systems, ITSM tools — without a wholesale migration.",
  },
  {
    icon: Workflow,
    title: "Orchestrate",
    desc: "Multi-agent pipelines with human control baked in: granular human-in-the-loop gates, approval flows, and execution observability. Every decision point is auditable.",
  },
  {
    icon: Database,
    title: "Deploy",
    desc: "Pre-built domain knowledge. Domain Packs — healthcare chart audit, insurance claims, IT Ops runbook automation, BFSI document processing, HR ticket triage — get you to production in 3–4 weeks.",
  },
];

// Smaller building blocks shown as a bento beside the RAG spotlight.
const buildingBlocks = [
  { icon: Zap, title: "Triggers", desc: "Webhooks, schedules, Drive folder watches, and file uploads — each webhook with a curl sample." },
  { icon: Brain, title: "AI Tasks & Prompt Lab", desc: "Classification, extraction, summarisation, and OCR, with prompts tested in Prompt Lab before you ship." },
  { icon: Plug, title: "Integrations & MCP", desc: "Native processors, OAuth2 apps (Microsoft 365, Salesforce, Slack), and MCP in both directions." },
  { icon: MessageSquare, title: "Composer", desc: "Describe the workflow you want in plain language; Composer drafts it on the canvas for you to review and refine." },
  { icon: Mic, title: "Audio & language", desc: "Transcribe calls and recordings, translate between languages, and turn text into speech — as workflow steps." },
  { icon: FileOutput, title: "Documents out", desc: "Generate DOCX from templates, render PDFs, and validate every output against a JSON schema before it moves on." },
];

const deployments = [
  { icon: Cloud, title: "SaaS", desc: "Fully managed by Ariviti. Fastest to start." },
  { icon: Server, title: "BYOC", desc: "Your AWS, Azure, or GCP. You keep data sovereignty." },
  { icon: Building2, title: "On-prem", desc: "Inside your perimeter, for data that can't leave." },
  { icon: Cpu, title: "Edge", desc: "On Jetson Orin NX, for latency-sensitive or air-gapped sites." },
];

const domainPacks = [
  { page: "solutions-healthcare", title: "Healthcare", desc: "Chart audit, prior auth, and clinical notes." },
  { page: "solutions-bfsi", title: "BFSI", desc: "Loan documents, KYC/AML, and credit memos." },
  { page: "solutions-insurance", title: "Insurance", desc: "Claims, underwriting, and complaints." },
  { page: "platform-chatbots", title: "Customer Support", desc: "Cited answers and hand-off to a person." },
  { page: "solutions-hr-tech", title: "HR Tech", desc: "Screening, support, and onboarding." },
  { page: "solutions-it-ops", title: "IT Ops", desc: "Alert correlation, RCA, and auto-remediation." },
];

const buildVsBuy = [
  { you: "Build orchestration, retries, and state from scratch", turfai: "Visual workflow engine, ready to run" },
  { you: "Wire up RBAC, audit logs, and cost tracking yourself", turfai: "Governance built into every step" },
  { you: "Hand-roll PII redaction before each model call", turfai: "Data Shield tokenises PII automatically" },
  { you: "Maintain connectors and a tool registry over time", turfai: "Governed integrations and MCP, maintained" },
];

export function PlatformPage({ onNavigate, onNavigateToDemo }: PlatformPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>Build workflows visually. Run them with agents. <Mark>Prove every step</Mark>.</>}
        lede="TurfAI pairs a visual workflow builder with agentic AI, RAG, and enterprise governance — so automations move from prototype to production on a single, auditable execution layer."
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <HeroLink onClick={() => (window.location.href = siteConfig.urls.developers)}>Developer docs</HeroLink>
          </>
        }
        visual={<RunConsole {...runs.workflow} />}
      />

      {/* Problem statement — the 80/20 truth */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Why most AI stalls</p>
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo">
              Most enterprise AI POCs never reach production. The bottleneck is <Mark>never the model</Mark>.
            </h2>
            <p className="text-[20px] text-soft-black mt-5 max-w-2xl mx-auto">
              The real obstacles are the infrastructure around the model — and the trust it earns.
              TurfAI is that infrastructure.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {infraGaps.map((g) => (
                <span key={g} className="px-4 py-2 rounded-full text-sm font-medium bg-indigo-tint-20 text-soft-black border border-softblack-tint-20">
                  {g}
                </span>
              ))}
            </div>
            <PageLink page={"platform-why"} onNavigate={onNavigate}
              className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-royal-indigo hover:text-dark-indigo"
            >
              Why TurfAI — the rails around the model <ArrowRight className="w-4 h-4" />
            </PageLink>
          </div>
        </div>
      </section>

      {/* Execution canvas — a connected flow */}
      <section className="py-16 lg:py-20 band-surface border-y border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-stretch gap-3">
            {canvas.map((step, i) => (
              <div key={step.label} className="flex items-center gap-3 flex-1">
                <Card className="p-5 border-softblack-tint-20 bg-white w-full h-full">
                  <CardContent className="p-0">
                    <div className="w-11 h-11 bg-indigo-tint-20 rounded-xl flex items-center justify-center mb-3">
                      <step.icon className="w-5 h-5 text-royal-indigo" />
                    </div>
                    <h3 className="font-semibold text-dark-indigo mb-1">{step.label}</h3>
                    <p className="text-sm text-soft-black">{step.desc}</p>
                  </CardContent>
                </Card>
                {i < canvas.length - 1 && (
                  <ChevronRight className="hidden lg:block w-6 h-6 shrink-0 text-indigo-tint-20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The rails around the model — layered stack */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">How it works</p>
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">The <Mark>rails</Mark> around the model</h2>
            <p className="text-[20px] text-soft-black">
              A model call is the easy part. TurfAI is the infrastructure that makes it trustworthy
              enough to put into production — three layers, from your systems up to packaged outcomes.
            </p>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {layers.map((layer, i) => (
              <div
                key={layer.title}
                className="flex flex-col sm:flex-row items-start gap-5 rounded-2xl border border-softblack-tint-20 bg-indigo-tint-05 p-6 lg:p-7"
              >
                <div className="flex items-center gap-4 sm:w-64 shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-indigo-tint-20 text-royal-indigo flex items-center justify-center">
                    <layer.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-soft-black">Layer {i + 1}</span>
                    <h3 className="text-[20px] font-semibold text-dark-indigo">{layer.title}</h3>
                  </div>
                </div>
                <p className="text-soft-black flex-1">{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agents & Squads spotlight — dark */}
      <section className="py-20 lg:py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="rounded-2xl bg-transparent border border-royal-indigo p-6 lg:p-8 order-2 lg:order-1">
              <PlatformGraphic name="agents" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm font-semibold uppercase tracking-wider text-indigo-tint-20 mb-4">AI Agents &amp; Squads</p>
              <h2 className="text-[30px] lg:text-[40px] leading-tight font-bold tracking-tight text-white mb-4">
                Agents that reason and act — <Mark>teams</Mark> when one isn't enough
              </h2>
              <p className="text-[20px] text-indigo-tint-20 mb-6">
                ReAct-style agents plan, call tools from a governed registry, and act. For complex work,
                multi-agent squads coordinate specialists toward a shared goal — with a human sign-off at the end.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant="onDark" className="hover:bg-softblack-tint-20" asChild><PageLink page={"platform-agents"} onNavigate={onNavigate}>
                  Explore AI Agents <ArrowRight className="w-4 h-4 ml-2" />
                </PageLink></Button>
                <Button variant="outlineOnDark" asChild><PageLink page={"platform-squads"} onNavigate={onNavigate}>
                  Agent Squads
                </PageLink></Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RAG spotlight + building blocks bento */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto mb-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">RAG &amp; Knowledge</p>
              <h2 className="text-[30px] lg:text-[40px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
                Answers your team can <Mark>trust</Mark>, with citations
              </h2>
              <p className="text-[20px] text-soft-black mb-6">
                Index your documents for semantic search, ask in natural language, and get responses that
                cite exactly where they came from — the retrieval layer behind support agents and squads.
              </p>
              <PageLink page={"platform-rag"} onNavigate={onNavigate}
                className="inline-flex items-center gap-1 text-sm font-semibold text-royal-indigo hover:text-dark-indigo"
              >
                Explore RAG &amp; Knowledge <ArrowRight className="w-4 h-4" />
              </PageLink>
            </div>
            <div className="rounded-2xl bg-dark-indigo border border-softblack-tint-20 p-6 lg:p-8">
              <PlatformGraphic name="rag" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {buildingBlocks.map((b) => (
              <Card key={b.title} className="p-6 bg-white border-softblack-tint-20 transition-shadow">
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-xl bg-indigo-tint-20 text-royal-indigo flex items-center justify-center mb-4">
                    <b.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-[20px] font-semibold text-dark-indigo mb-2">{b.title}</h3>
                  <p className="text-sm text-soft-black leading-relaxed">{b.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Shield / governance — flagship trust section */}
      <section className="py-20 lg:py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-14">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-indigo-tint-20 mb-4">Governance &amp; Data Shield</p>
                <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-white mb-4">
                  Sensitive data <Mark>never reaches the model</Mark> in the clear
                </h2>
                <p className="text-[20px] leading-relaxed text-indigo-tint-20 mb-6">
                  Data Shield detects and tokenises personal data before a workflow, agent, or extraction call
                  reaches a model. The model sees tokens; your systems get the real values back. Every shielded call writes an immutable audit row with
                  entity counts, never the raw data itself.
                </p>
                <ul className="space-y-3">
                  {[
                    "Fine-grained RBAC and resource ownership, SSO via SAML 2.0 / OIDC",
                    "Immutable, queryable audit log on every workflow, agent action, and approval",
                    "Token-consumption and cost tracking in USD, per workflow and per team",
                    "Encryption in transit (TLS 1.3) and at rest; secrets held in your KMS / vault",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-3 text-indigo-tint-20">
                      <Lock className="w-5 h-5 text-indigo-tint-20 flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-transparent border border-royal-indigo p-6 lg:p-8">
                <PlatformGraphic name="shield" className="mb-6" />
                <div className="space-y-3 font-mono text-sm">
                  <div className="text-xs uppercase tracking-wider text-indigo-tint-20 mb-1">What the model sees</div>
                  <div className="p-3 rounded-lg bg-indigo-shade-80/30 text-indigo-tint-20">
                    email: <span className="text-indigo-tint-20">EMAIL_84d0a133</span><br />
                    pan: <span className="text-indigo-tint-20">PAN_c95eb795</span>
                  </div>
                  <div className="text-xs uppercase tracking-wider text-indigo-tint-20 pt-1">Restored for you</div>
                  <div className="p-3 rounded-lg bg-indigo-shade-80/30 text-white">
                    email: jane@acme.com<br />
                    pan: ABCDE1234F
                  </div>
                </div>
              </div>
            </div>
            {/* Fail-closed posture */}
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: "Fail-closed by default", desc: "If the PII detector errors, the call is blocked — never sent unshielded." },
                { title: "Compliance-ready", desc: "ISO 27001, ISO 9001, CMMI Level 3, HIPAA, GDPR and CASA — with a shared-responsibility matrix on request." },
                { title: "Your boundary", desc: "SaaS, BYOC/VPC, or on-prem — runtime data stays inside your environment." },
              ].map((c) => (
                <div key={c.title} className="rounded-2xl bg-transparent border border-royal-indigo p-6">
                  <h3 className="font-semibold text-white mb-1">{c.title}</h3>
                  <p className="text-sm text-indigo-tint-20 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button variant="onDark" size="lg" className="hover:bg-softblack-tint-20" asChild><PageLink page={"security"} onNavigate={onNavigate}>
                Explore security &amp; trust <ArrowRight className="w-4 h-4 ml-2" />
              </PageLink></Button>
            </div>
          </div>
        </div>
      </section>

      {/* Deployment tiles */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Deploy it <Mark>where your data lives</Mark></h2>
            <p className="text-[20px] text-soft-black max-w-2xl mx-auto">
              Same platform, your choice of boundary. Packs deploy wherever TurfAI runs.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {deployments.map((d) => (
              <Card key={d.title} className="p-4 sm:p-6 text-center border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-indigo-tint-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <d.icon className="w-6 h-6 text-royal-indigo" />
                  </div>
                  <h3 className="font-semibold text-dark-indigo mb-1">{d.title}</h3>
                  <p className="text-sm text-soft-black">{d.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Packs */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Domain Packs</p>
              <h2 className="text-[30px] lg:text-[40px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
                A production <Mark>starting point</Mark>, not another pilot
              </h2>
              <p className="text-[20px] text-soft-black mb-6">
                Pre-built, tuned agents, workflows, and squads for your industry — installed as pure
                configuration. Deploy as-is or fork to fit your process.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {domainPacks.map((p) => (
                  <PageLink page={p.page} onNavigate={onNavigate}
                    key={p.page}
                    className="group text-left rounded-xl bg-white border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all p-4"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-dark-indigo group-hover:text-royal-indigo transition-colors">{p.title}</span>
                      <ArrowUpRight className="w-4 h-4 text-indigo-tint-20 group-hover:text-royal-indigo transition-colors" />
                    </div>
                    <p className="text-sm text-soft-black leading-snug">{p.desc}</p>
                  </PageLink>
                ))}
              </div>
              <PageLink page={"platform-domain-packs"} onNavigate={onNavigate}
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-royal-indigo hover:text-dark-indigo"
              >
                How Domain Packs work <ArrowRight className="w-4 h-4" />
              </PageLink>
            </div>
            <div className="rounded-2xl bg-dark-indigo p-6 lg:p-10 order-first lg:order-last">
              <PlatformGraphic name="packs" />
            </div>
          </div>
        </div>
      </section>

      {/* Build vs buy */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Buy the <Mark>rails</Mark>. Build the workflows.</h2>
            <p className="text-[20px] text-soft-black max-w-2xl mx-auto">
              You could build all of this in-house. Here's what you'd own before shipping a single workflow.
            </p>
          </div>
          <div className="max-w-4xl mx-auto grid gap-4">
            {buildVsBuy.map((row) => (
              <div key={row.you} className="grid md:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-indigo-tint-05 border border-softblack-tint-20 text-soft-black text-sm">
                  Build it yourself: {row.you}
                </div>
                <div className="p-5 rounded-xl bg-white border border-indigo-tint-50 text-sm text-soft-black flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-royal-indigo flex-shrink-0 mt-0.5" />
                  <span>On TurfAI: {row.turfai}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">See the platform <Mark>in action</Mark></h2>
          <p className="text-[22px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Explore the solutions teams are already shipping on TurfAI, or talk to us about yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
              Schedule a demo
            </Button>
            <Button variant="outlineOnDark" size="lg" asChild><PageLink page={"solutions"} onNavigate={onNavigate}>
              See solutions <ArrowRight className="w-4 h-4 ml-2" />
            </PageLink></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
